import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchConversations,
  fetchMessages,
  sendMessage,
  markAsRead,
  createConversation,
  setActiveConversation,
  clearActiveConversation,
  addMessageFromSocket,
  incrementUnreadCount,
  incrementConversationUnread,
  setTypingUser,
  clearTypingUser,
  fetchChatMembers,
  updateOtherLastRead,
} from '../../redux/Slices/chatSlice';
import { OnlineStatusContext } from '../../context/OnlineStatusContext';
import { useSocketEvent } from '../../hooks/useSocketManager';
import socketManager from '../../utils/SocketManager';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import EmojiPicker from '../Popovers/EmojiPicker';
import { config } from '../../config';
import { fetchWithCSRF } from '../../utils/apiUtils';
import './ChatColumn.scss';
import Search from '../icons/Search';
import Messages from '../icons/Messages';

// Skeleton loaders
const ChatRowSkeleton = () => (
  <div className="chat-skeleton-row">
    <div className="chat-skeleton chat-skeleton--circle" />
    <div className="chat-skeleton-body">
      <div className="chat-skeleton chat-skeleton--text-short" />
      <div className="chat-skeleton chat-skeleton--text-long" />
    </div>
  </div>
);

const ChatListSkeleton = () => (
  <div className="chat-skeleton-list">
    {[...Array(6)].map((_, i) => <ChatRowSkeleton key={i} />)}
  </div>
);

const ChatBubbleSkeleton = ({ align = 'left' }) => (
  <div className={`chat-skeleton-bubble chat-skeleton-bubble--${align}`}>
    <div className="chat-skeleton chat-skeleton--bubble" />
  </div>
);

const ChatMessagesSkeleton = () => (
  <div className="chat-skeleton-messages">
    <ChatBubbleSkeleton align="left" />
    <ChatBubbleSkeleton align="left" />
    <ChatBubbleSkeleton align="right" />
    <ChatBubbleSkeleton align="left" />
    <ChatBubbleSkeleton align="right" />
    <ChatBubbleSkeleton align="right" />
  </div>
);

const ChatColumn = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { board } = useSelector((state) => state.board);
  const {
    conversations,
    activeConversation,
    messages,
    messagesLoading,
    conversationsLoading,
    typingUsers,
    hasMoreMessages,
    members: workspaceMembers,
    membersLoading,
    otherLastRead,
  } = useSelector((state) => state.chat);

  const { workspaces } = useSelector((state) => state.workspaces);
  const onlineUsers = useContext(OnlineStatusContext);
  const currentUserId = user?.user?._id;
  const workspaceId = board?.wShortId || workspaces?.[0]?.shortId;

  const [activeView, setActiveView] = useState('list');
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearch, setGifSearch] = useState('');
  const [gifResults, setGifResults] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  // Fetch conversations — runs on mount and whenever workspaceId becomes available
  useEffect(() => {
    if (workspaceId && activeView === 'list') {
      dispatch(fetchConversations({ workspaceId }));
    }
  }, [workspaceId, dispatch, activeView]);

  useEffect(() => {
    if (activeConversation) {
      setActiveView('chat');
      if (messages.length === 0) {
        dispatch(fetchMessages({ conversationId: activeConversation.shortId }));
        socketManager.emit('chat:join', activeConversation.shortId);
      }
    }
  }, [activeConversation?.shortId]);

  useEffect(() => {
    if (messagesEndRef.current && activeView === 'chat') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, activeView]);

  useSocketEvent('chat:message', (data) => {
    if (!data?.message) return;
    if (data.message.sender?._id === currentUserId) return;
    dispatch(addMessageFromSocket(data));
    // Auto mark as read since the chat is already open
    if (activeConversation?.shortId === data.conversationId) {
      dispatch(markAsRead({ conversationId: data.conversationId }));
    }
  }, [currentUserId, activeConversation?.shortId]);

  useSocketEvent('chat:newMessage', (data) => {
    if (!data?.conversationId || !data?.message) return;
    if (data.message.sender?._id === currentUserId) return;

    // Always add the message (fallback if chat:message room event was missed)
    dispatch(addMessageFromSocket(data));

    if (activeConversation?.shortId === data.conversationId) {
      // Active conversation — mark as read, no unread increment
      dispatch(markAsRead({ conversationId: data.conversationId }));
    } else {
      // Different conversation — increment unread
      dispatch(incrementUnreadCount());
      dispatch(incrementConversationUnread({ conversationId: data.conversationId }));
    }
  }, [activeConversation?.shortId, currentUserId]);

  // Socket: seen receipt — other user read our messages
  useSocketEvent('chat:seen', (data) => {
    if (data?.userId !== currentUserId && data?.readAt && data?.conversationId) {
      dispatch(updateOtherLastRead({ conversationId: data.conversationId, readAt: data.readAt }));
    }
  }, [currentUserId]);

  useSocketEvent('chat:typing', (data) => {
    if (data?.userId !== currentUserId) {
      dispatch(setTypingUser(data));
      setTimeout(() => dispatch(clearTypingUser(data)), 3000);
    }
  }, [currentUserId]);

  useSocketEvent('chat:stopTyping', (data) => {
    if (data?.userId !== currentUserId) {
      dispatch(clearTypingUser(data));
    }
  }, [currentUserId]);

  const handleOpenConversation = useCallback((conversation) => {
    dispatch(setActiveConversation(conversation));
    // Always re-fetch messages to get fresh otherLastRead for seen indicators
    dispatch(fetchMessages({ conversationId: conversation.shortId }));
    dispatch(markAsRead({ conversationId: conversation.shortId }));
    socketManager.emit('chat:join', conversation.shortId);
  }, [dispatch]);

  const handleBack = useCallback(() => {
    if (activeConversation) {
      socketManager.emit('chat:leave', activeConversation.shortId);
    }
    dispatch(clearActiveConversation());
    setActiveView('list');
    setInputValue('');
    if (workspaceId) dispatch(fetchConversations({ workspaceId }));
  }, [activeConversation, dispatch, workspaceId]);

  const handleSend = useCallback(() => {
    const content = inputValue.trim();
    if (!content || !activeConversation) return;

    dispatch(sendMessage({
      conversationId: activeConversation.shortId,
      content,
      tempId: `temp-${Date.now()}`,
      senderInfo: {
        _id: currentUserId,
        name: user?.user?.name,
        username: user?.user?.username,
        profilePicture: user?.user?.profilePicture,
      },
    }));
    setInputValue('');

    if (isTypingRef.current) {
      socketManager.emit('chat:stopTyping', {
        conversationId: activeConversation.shortId,
        userId: currentUserId,
      });
      isTypingRef.current = false;
    }
  }, [inputValue, activeConversation, currentUserId, dispatch]);

  const handleEmojiSelect = useCallback((emoji) => {
    setInputValue(prev => prev + emoji.native);
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  }, []);

  // File/image upload
  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeConversation) return;
    e.target.value = '';

    // Optimistic: show preview immediately
    const isImage = file.type.startsWith('image/');
    const tempId = `temp-${Date.now()}`;
    const previewUrl = isImage ? URL.createObjectURL(file) : null;

    dispatch(addMessageFromSocket({
      conversationId: activeConversation.shortId,
      message: {
        _id: tempId,
        shortId: tempId,
        content: '',
        type: isImage ? 'image' : 'file',
        sender: { _id: currentUserId, name: user?.user?.name, profilePicture: user?.user?.profilePicture },
        attachments: [{ url: previewUrl || '', name: file.name, type: file.type, size: file.size }],
        createdAt: new Date().toISOString(),
        _isOptimistic: true,
      },
    }));

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${config.API_URI}/v1/chat/${activeConversation.shortId}/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        // Replace optimistic with real message (socket skips own messages)
        dispatch({ type: 'chat/replaceOptimisticMessage', payload: { tempId, message: data.message } });
      }
    } catch (err) {
      console.error('[Chat] Upload error:', err);
    } finally {
      setUploading(false);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    }
  }, [activeConversation, currentUserId, user, dispatch]);

  // GIF search via Tenor
  const handleGifSearch = useCallback(async (query) => {
    setGifSearch(query);
    if (!query.trim()) {
      // Load trending
      try {
        const res = await fetch(`https://tenor.googleapis.com/v2/featured?key=AIzaSyAyimkuYQYF_FXVALexPuGQctUWRURdCYQ&limit=20&media_filter=gif`);
        const data = await res.json();
        setGifResults(data.results || []);
      } catch { setGifResults([]); }
      return;
    }
    try {
      const res = await fetch(`https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(query)}&key=AIzaSyAyimkuYQYF_FXVALexPuGQctUWRURdCYQ&limit=20&media_filter=gif`);
      const data = await res.json();
      setGifResults(data.results || []);
    } catch { setGifResults([]); }
  }, []);

  const handleOpenGifPicker = useCallback(() => {
    setShowGifPicker(true);
    setShowEmojiPicker(false);
    handleGifSearch(''); // load trending
  }, [handleGifSearch]);

  const handleSendGif = useCallback(async (gif) => {
    if (!activeConversation) return;
    setShowGifPicker(false);

    const gifUrl = gif.media_formats?.gif?.url || gif.media_formats?.mediumgif?.url;
    if (!gifUrl) return;

    try {
      await fetchWithCSRF(`${config.API_URI}/v1/chat/${activeConversation.shortId}/gif`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gifUrl }),
      });
    } catch (err) {
      console.error('[Chat] GIF send error:', err);
    }
  }, [activeConversation]);

  // Voice recording
  const handleStartRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });

        if (!activeConversation) return;
        setUploading(true);
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('messageType', 'audio');

          await fetch(`${config.API_URI}/v1/chat/${activeConversation.shortId}/upload`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });
        } catch (err) {
          console.error('[Chat] Voice upload error:', err);
        } finally {
          setUploading(false);
        }
      };

      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('[Chat] Microphone access denied:', err);
    }
  }, [activeConversation]);

  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    setInputValue(e.target.value);
    if (!activeConversation) return;

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socketManager.emit('chat:typing', {
        conversationId: activeConversation.shortId,
        userId: currentUserId,
      });
    }

    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      if (isTypingRef.current) {
        socketManager.emit('chat:stopTyping', {
          conversationId: activeConversation.shortId,
          userId: currentUserId,
        });
        isTypingRef.current = false;
      }
    }, 2000);
  }, [activeConversation, currentUserId]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || !hasMoreMessages || messagesLoading) return;
    if (container.scrollTop === 0 && messages.length > 0) {
      dispatch(fetchMessages({
        conversationId: activeConversation.shortId,
        before: messages[0].createdAt,
      }));
    }
  }, [hasMoreMessages, messagesLoading, messages, activeConversation, dispatch]);

  const handleNewChat = useCallback(() => {
    setActiveView('newChat');
    setSearchQuery('');
    dispatch(fetchChatMembers({
      boardId: board?.shortId,
      workspaceId,
      currentUserId,
    }));
  }, [board?.shortId, workspaceId, currentUserId, dispatch]);

  const handleSelectMember = useCallback(async (member) => {
    if (!board?.shortId) return;
    try {
      const action = await dispatch(createConversation({
        participantId: member._id,
        boardId: board.shortId,
        workspaceId,
      }));
      if (createConversation.fulfilled.match(action)) {
        const conv = action.payload.conversation;
        dispatch(fetchMessages({ conversationId: conv.shortId }));
        dispatch(markAsRead({ conversationId: conv.shortId }));
        socketManager.emit('chat:join', conv.shortId);
        setActiveView('chat');
      }
    } catch (err) {
      console.error('[Chat] Error:', err);
    }
  }, [workspaceId, board?.shortId, dispatch]);

  // Avatar renderer
  const renderAvatar = (u, size = 40) => {
    if (!u) return <div className="chat-avatar-placeholder" style={{ width: size, height: size }} />;
    if (u.profilePicture) {
      return <img src={u.profilePicture} alt={u.name || ''} className="chat-avatar-img" style={{ width: size, height: size }} loading="lazy" />;
    }
    return <ImagePlaceholder size={size} text={u.username || u.name || '?'} onlineIndicator={false} />;
  };

  const getOtherParticipant = (conversation) => {
    if (!conversation?.participants) return null;
    return conversation.participants.find(p => (p.user?._id || p.user) !== currentUserId)?.user;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  };

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // Format date separator ("Today", "Yesterday", "Mar 13, 2026")
  const formatDateSeparator = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diff = today - msgDay;

    if (diff === 0) return 'Today';
    if (diff === 86400000) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Check if two dates are on different days
  const isDifferentDay = (d1, d2) => {
    if (!d1 || !d2) return true;
    const a = new Date(d1), b = new Date(d2);
    return a.getFullYear() !== b.getFullYear() || a.getMonth() !== b.getMonth() || a.getDate() !== b.getDate();
  };

  // Check if there's a time gap (>5 min) between messages
  const hasTimeGap = (msg, prevMsg) => {
    if (!prevMsg) return true;
    return new Date(msg.createdAt) - new Date(prevMsg.createdAt) > 5 * 60 * 1000;
  };

  // ========== CONVERSATION LIST ==========
  const renderConversationList = () => (
    <div className="chat-list">
      <div className="chat-list__header">
        <button className="chat-list__new-btn" onClick={handleNewChat}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
          New Message
        </button>
      </div>

      {conversationsLoading ? (
        <ChatListSkeleton />
      ) : conversations.length === 0 ? (
        <div className="chat-empty">
          <div className="chat-empty__icon">
            <Messages size={48} color="var(--secondary-text-color)" />
          </div>
          <p>No conversations yet</p>
          <span>Start a new message to chat with your team</span>
        </div>
      ) : (
        <div className="chat-list__items">
          {conversations.map((conv) => {
            const other = getOtherParticipant(conv);
            if (!other) return null;
            const isOnline = onlineUsers?.[other._id] || other.online === true;
            const hasUnread = conv.unreadCount > 0;

            return (
              <div key={conv._id || conv.shortId} className={`chat-row ${hasUnread ? 'chat-row--unread' : ''}`} onClick={() => handleOpenConversation(conv)}>
                <div className="chat-row__avatar">
                  {renderAvatar(other, 48)}
                  {isOnline && <span className="chat-row__online" />}
                </div>
                <div className="chat-row__body">
                  <div className="chat-row__top">
                    <span className="chat-row__name">{other.name}</span>
                    <span className={`chat-row__time ${hasUnread ? 'chat-row__time--unread' : ''}`}>
                      {formatTime(conv.lastMessage?.timestamp || conv.updatedAt)}
                    </span>
                  </div>
                  <div className="chat-row__bottom">
                    <span className={`chat-row__preview ${hasUnread ? 'chat-row__preview--unread' : ''}`}>
                      {conv.lastMessage?.content || 'No messages yet'}
                    </span>
                    {hasUnread && <span className="chat-row__badge">{conv.unreadCount}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ========== NEW CHAT ==========
  const renderNewChat = () => {
    const filtered = workspaceMembers.filter(m =>
      m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.username?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="chat-new">
        <div className="chat-new__header">
          <button className="chat-back-btn" onClick={() => setActiveView('list')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="chat-new__title">New Message</span>
        </div>
        <div className="chat-new__search">
          <Search className="chat-new__search-icon" size={16} />
          <input type="text" placeholder="Search contacts..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} autoFocus />
        </div>
        {membersLoading ? (
          <ChatListSkeleton />
        ) : (
          <div className="chat-new__list">
            {filtered.map((member) => {
              const isOnline = onlineUsers?.[member._id];
              return (
                <div key={member._id} className="chat-contact" onClick={() => handleSelectMember(member)}>
                  <div className="chat-contact__avatar">
                    {renderAvatar(member, 44)}
                    {isOnline && <span className="chat-contact__online" />}
                  </div>
                  <div className="chat-contact__info">
                    <span className="chat-contact__name">{member.name}</span>
                    <span className="chat-contact__sub">@{member.username}</span>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <div className="chat-empty"><p>No members found</p></div>}
          </div>
        )}
      </div>
    );
  };

  // ========== ACTIVE CHAT ==========
  const renderActiveChat = () => {
    const other = getOtherParticipant(activeConversation);
    const isOnline = other ? (onlineUsers?.[other._id] || other.online === true) : false;
    const typing = activeConversation ? typingUsers[activeConversation.shortId] || [] : [];

    return (
      <div className="chat-thread">
        {/* Header */}
        <div className="chat-thread__header">
          <button className="chat-back-btn" onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          {other && (
            <div className="chat-thread__user">
              <div className="chat-thread__user-avatar">
                {renderAvatar(other, 36)}
                {isOnline && <span className="chat-thread__online" />}
              </div>
              <div className="chat-thread__user-info">
                <span className="chat-thread__user-name">{other.name}</span>
                <span className={`chat-thread__user-status ${isOnline ? 'online' : ''}`}>
                  {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="chat-thread__messages" ref={messagesContainerRef} onScroll={handleScroll}>
          {messagesLoading && messages.length === 0 ? (
            <ChatMessagesSkeleton />
          ) : messages.length === 0 ? (
            <div className="chat-empty">
              <p>No messages yet</p>
              <span>Say hello to start the conversation</span>
            </div>
          ) : (
            <>
              {hasMoreMessages && messagesLoading && (
                <div className="chat-skeleton-messages" style={{ padding: '8px 16px' }}>
                  <ChatBubbleSkeleton align="left" />
                  <ChatBubbleSkeleton align="right" />
                </div>
              )}
              {(() => {
                // Find the last self-message that was seen (per conversation)
                const convLastRead = activeConversation?.shortId ? otherLastRead[activeConversation.shortId] : null;
                let lastSeenIdx = -1;
                if (convLastRead) {
                  for (let i = messages.length - 1; i >= 0; i--) {
                    const m = messages[i];
                    const isSelfMsg = m.sender?._id === currentUserId || m.sender?._id === 'self';
                    if (isSelfMsg && !m._isOptimistic && new Date(m.createdAt) <= new Date(convLastRead)) {
                      lastSeenIdx = i;
                      break;
                    }
                  }
                }

                return messages.map((msg, idx) => {
                  const isSelf = msg.sender?._id === currentUserId || msg.sender?._id === 'self';
                  const prevMsg = messages[idx - 1];
                  const nextMsg = messages[idx + 1];
                  const prevIsSelf = prevMsg && (prevMsg.sender?._id === currentUserId || prevMsg.sender?._id === 'self');
                  const nextIsSelf = nextMsg && (nextMsg.sender?._id === currentUserId || nextMsg.sender?._id === 'self');

                  const isConsecutive = prevMsg && (isSelf === prevIsSelf) && !hasTimeGap(msg, prevMsg) && !isDifferentDay(msg.createdAt, prevMsg.createdAt);
                  const isLastInGroup = !nextMsg || (isSelf !== nextIsSelf) || hasTimeGap(nextMsg, msg) || isDifferentDay(nextMsg?.createdAt, msg.createdAt);
                  const showDateSep = isDifferentDay(msg.createdAt, prevMsg?.createdAt);
                  const showTimestamp = isLastInGroup;
                  const showAvatar = !isSelf && isLastInGroup;
                  const isSeen = isSelf && idx === lastSeenIdx;

                  return (
                    <React.Fragment key={msg._id || msg.shortId || idx}>
                      {/* Date separator */}
                      {showDateSep && (
                        <div className="chat-date-sep">
                          <span>{formatDateSeparator(msg.createdAt)}</span>
                        </div>
                      )}

                      <div className={`chat-bubble-wrap ${isSelf ? 'chat-bubble-wrap--self' : 'chat-bubble-wrap--other'} ${isConsecutive ? 'chat-bubble-wrap--consecutive' : ''} ${msg._isOptimistic ? 'chat-bubble-wrap--sending' : ''}`}>
                        {/* Avatar — only on last message in other's group */}
                        {!isSelf && (
                          <div className="chat-bubble-wrap__avatar">
                            {showAvatar ? renderAvatar(other, 28) : <div style={{ width: 28 }} />}
                          </div>
                        )}

                        <div className="chat-bubble-wrap__content">
                          {/* Image message */}
                          {msg.type === 'image' && msg.attachments?.[0] && (
                            <div className={`chat-bubble chat-bubble--media ${isSelf ? 'chat-bubble--self' : 'chat-bubble--other'}`}>
                              <img src={msg.attachments[0].url} alt="" className="chat-bubble__image" loading="lazy" onClick={() => window.open(msg.attachments[0].url, '_blank')} />
                              {!showTimestamp && <span className="chat-bubble__hover-time">{formatMessageTime(msg.createdAt)}</span>}
                            </div>
                          )}

                          {/* GIF message */}
                          {msg.type === 'gif' && msg.attachments?.[0] && (
                            <div className={`chat-bubble chat-bubble--media ${isSelf ? 'chat-bubble--self' : 'chat-bubble--other'}`}>
                              <img src={msg.attachments[0].url} alt="GIF" className="chat-bubble__gif" loading="lazy" />
                              {!showTimestamp && <span className="chat-bubble__hover-time">{formatMessageTime(msg.createdAt)}</span>}
                            </div>
                          )}

                          {/* Audio message */}
                          {msg.type === 'audio' && msg.attachments?.[0] && (
                            <div className={`chat-bubble ${isSelf ? 'chat-bubble--self' : 'chat-bubble--other'}`}>
                              <audio controls src={msg.attachments[0].url} className="chat-bubble__audio" />
                              {!showTimestamp && <span className="chat-bubble__hover-time">{formatMessageTime(msg.createdAt)}</span>}
                            </div>
                          )}

                          {/* File message */}
                          {msg.type === 'file' && msg.attachments?.[0] && (
                            <div className={`chat-bubble ${isSelf ? 'chat-bubble--self' : 'chat-bubble--other'}`}>
                              <a href={msg.attachments[0].url} target="_blank" rel="noopener noreferrer" className="chat-bubble__file">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
                                <span>{msg.attachments[0].name}</span>
                              </a>
                              {!showTimestamp && <span className="chat-bubble__hover-time">{formatMessageTime(msg.createdAt)}</span>}
                            </div>
                          )}

                          {/* Text message (default) */}
                          {(msg.type === 'text' || msg.type === 'system' || !msg.type) && (
                            <div className={`chat-bubble ${isSelf ? 'chat-bubble--self' : 'chat-bubble--other'}`}>
                              <p className="chat-bubble__text">{msg.content}</p>
                              {!showTimestamp && <span className="chat-bubble__hover-time">{formatMessageTime(msg.createdAt)}</span>}
                            </div>
                          )}

                          {/* Seen indicator — only on last seen self-message */}
                          {isSeen && (
                            <div className={`chat-bubble__meta chat-bubble__meta--self`}>
                              <span className="chat-bubble__seen-text">Seen</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Typing indicator */}
        {typing.length > 0 && (
          <div className="chat-thread__typing">
            <div className="typing-dots">
              <span /><span /><span />
            </div>
          </div>
        )}

        {/* Emoji picker */}
        {showEmojiPicker && (
          <div className="chat-thread__emoji-picker">
            <EmojiPicker onEmojiSelect={handleEmojiSelect} />
          </div>
        )}

        {/* GIF picker */}
        {showGifPicker && (
          <div className="chat-thread__gif-picker">
            <input
              type="text"
              className="chat-thread__gif-search"
              placeholder="Search GIFs..."
              value={gifSearch}
              onChange={(e) => handleGifSearch(e.target.value)}
              autoFocus
            />
            <div className="chat-thread__gif-grid">
              {gifResults.map((gif) => (
                <img
                  key={gif.id}
                  src={gif.media_formats?.tinygif?.url || gif.media_formats?.gif?.url}
                  alt=""
                  className="chat-thread__gif-item"
                  onClick={() => handleSendGif(gif)}
                />
              ))}
              {gifResults.length === 0 && <div className="chat-empty" style={{ padding: '20px' }}><p>No GIFs found</p></div>}
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.zip,.rar"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* Uploading indicator */}
        {uploading && (
          <div className="chat-thread__uploading">
            <span>Uploading...</span>
          </div>
        )}

        {/* Recording indicator */}
        {isRecording && (
          <div className="chat-thread__recording">
            <span className="chat-thread__recording-dot" />
            <span>Recording...</span>
            <button className="chat-thread__recording-stop" onClick={handleStopRecording}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
              Send
            </button>
          </div>
        )}

        {/* Input bar */}
        {!isRecording && (
          <div className="chat-thread__input">
            {inputValue.trim() ? (
              <>
                <button className="chat-thread__action-btn" onClick={() => { setShowEmojiPicker(false); setShowGifPicker(false); fileInputRef.current?.click(); }} data-tooltip-content="Attach" data-tooltip-position="top">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                </button>
                <div className="chat-thread__input-field">
                  <textarea ref={textareaRef} value={inputValue} onChange={handleInputChange} onKeyDown={handleKeyDown} onFocus={() => { setShowEmojiPicker(false); setShowGifPicker(false); }} placeholder="Aa" rows={1} />
                  <button className="chat-thread__emoji-btn" onClick={() => { setShowEmojiPicker(prev => !prev); setShowGifPicker(false); }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                  </button>
                </div>
                <button className="chat-thread__send" onClick={handleSend}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                </button>
              </>
            ) : (
              <>
                <div className="chat-thread__input-actions">
                  <button className="chat-thread__action-btn" onClick={handleStartRecording} data-tooltip-content="Voice message" data-tooltip-position="top">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>
                  </button>
                  <button className="chat-thread__action-btn" onClick={() => { setShowEmojiPicker(false); setShowGifPicker(false); fileInputRef.current?.click(); }} data-tooltip-content="Photo / File" data-tooltip-position="top">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                  </button>
                  <button className="chat-thread__action-btn" onClick={() => { setShowEmojiPicker(prev => !prev); setShowGifPicker(false); }} data-tooltip-content="Sticker" data-tooltip-position="top">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/></svg>
                  </button>
                  <button className="chat-thread__action-btn" onClick={handleOpenGifPicker} data-tooltip-content="GIF" data-tooltip-position="top">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-8.5 8.5H9v1h1.5c.28 0 .5.22.5.5v1c0 .28-.22.5-.5.5H8.5c-.28 0-.5-.22-.5-.5v-4c0-.28.22-.5.5-.5H11c.28 0 .5.22.5.5v.5zm3 3h-1c-.28 0-.5-.22-.5-.5V9c0-.28.22-.5.5-.5h1c.28 0 .5.22.5.5v5c0 .28-.22.5-.5.5zm4-3h-1v.5h1c.28 0 .5.22.5.5s-.22.5-.5.5h-1v1c0 .28-.22.5-.5.5s-.5-.22-.5-.5V9c0-.28.22-.5.5-.5h2c.28 0 .5.22.5.5s-.22.5-.5.5z"/></svg>
                  </button>
                </div>
                <div className="chat-thread__input-field">
                  <textarea ref={textareaRef} value={inputValue} onChange={handleInputChange} onKeyDown={handleKeyDown} onFocus={() => { setShowEmojiPicker(false); setShowGifPicker(false); }} placeholder="Aa" rows={1} />
                  <button className="chat-thread__emoji-btn" onClick={() => { setShowEmojiPicker(prev => !prev); setShowGifPicker(false); }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                  </button>
                </div>
                <button className="chat-thread__action-btn chat-thread__like-btn" onClick={() => { setInputValue('👍'); setTimeout(handleSend, 0); }} data-tooltip-content="Like" data-tooltip-position="top">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M2 20h2V8H2v12zm22-11a2 2 0 0 0-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 0 7.59 6.59C7.22 6.95 7 7.45 7 8v10a2 2 0 0 0 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/></svg>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="chat-column">
      {activeView === 'list' && renderConversationList()}
      {activeView === 'newChat' && renderNewChat()}
      {activeView === 'chat' && activeConversation && renderActiveChat()}
    </div>
  );
};

export default ChatColumn;
